import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Sparkles, RefreshCw, BarChart3, AlertTriangle, Cpu, GitBranch,
    Database, FileSpreadsheet, Printer, Layers, ChevronDown, ChevronRight,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PAYLOAD = {
    requestContext: {
        clientId: "sitecentric",
        tenantId: "tenant_sitecentric_orl",
        companyId: "sitecentric",
        siteIds: ["site_orl"],
        requestedByUserId: "qa_user",
        requestId: "req_qa_001",
        timezone: "America/New_York",
    },
    filters: {
        period: { type: "quarter", label: "Q2 2026", startDate: "2026-04-01", endDate: "2026-06-30" },
        comparisonPeriod: { type: "previous_quarter", label: "Q1 2026", startDate: "2026-01-01", endDate: "2026-03-31" },
        sponsorCro: "all",
        indication: "all",
        includeArchived: false,
    },
    dashboardParameters: {
        growthTargetYear: 2027,
        growthTargetPercent: 30,
        growthBasisMetric: "initiated_studies",
        relationshipLookbackMonths: 18,
        fqToInitiatedPredictionMonths: 6,
        leadIndicationTrendMonths: 6,
        minimumSampleSizeForInsight: 5,
    },
    data: {
        opportunities: [
            {
                opportunityId: "opp_001",
                leadId: "lead_001",
                studyStartSiteId: "sss_001",
                studyId: "study_001",
                protocolId: "protocol_001",
                protocolNumber: "ABC-301",
                studyTitle: "Sample Dermatology Study",
                companyId: "sitecentric",
                siteId: "site_orl",
                siteName: "ORL",
                leadCreatedDate: "2026-04-03",
                sponsor: { sponsorId: "sponsor_001", name: "Sample Sponsor" },
                cro: { croId: "cro_001", name: "Sample CRO" },
                leadSource: {
                    sourceId: "source_001",
                    category: "existing_sponsor_relationship",
                    label: "Direct from existing Sponsor",
                    relationshipExisting: true,
                },
                indication: { indicationId: "ind_derm", name: "Dermatology" },
                therapeuticArea: "Dermatology",
                currentStatus: "active",
                currentStage: "initiated",
                milestones: {
                    feasibilityReceivedExecutedDate: "2026-04-07",
                    preSiteSelectionDate: "2026-04-22",
                    siteSelectionReceivedDate: "2026-05-14",
                    greenLightDate: "2026-06-01",
                    fpvDate: null,
                },
                status: {
                    isPending: false, isDeclined: false, isCancelled: false,
                    declinedBy: null, cancelledBy: null, stageAtDeclineOrCancel: null,
                    reasonCode: null, reasonLabel: null,
                },
            },
        ],
        historicalAggregates: {
            currentPeriod: { leadCount: 240, fqCount: 140, sqvCount: 90, awardedTrialCount: 60, initiatedCount: 50 },
            comparisonPeriod: { leadCount: 210, fqCount: 128, sqvCount: 85, awardedTrialCount: 56, initiatedCount: 45 },
        },
    },
};

const MODE_META = {
    analyze: { label: "Analyze", icon: BarChart3, path: "/v1/bd-funnel/analyze" },
    "insight-drilldown": { label: "Insight Drilldown", icon: Layers, path: "/v1/bd-funnel/insight-drilldown" },
    "print-report": { label: "Print Report", icon: Printer, path: "/v1/bd-funnel/print-report" },
};

const STATUS_TONE = {
    success: "border-emerald-400/30 text-emerald-300 bg-emerald-400/5",
    pending: "border-amber-400/30 text-amber-300 bg-amber-400/5",
    declined_site: "border-rose-400/30 text-rose-300 bg-rose-400/5",
    declined_sponsor_cro: "border-rose-400/30 text-rose-300 bg-rose-400/5",
    cancelled_sponsor: "border-rose-400/30 text-rose-300 bg-rose-400/5",
    cancelled_cro: "border-rose-400/30 text-rose-300 bg-rose-400/5",
};

const SEVERITY_TONE = {
    info: "border-sky-400/30 text-sky-300 bg-sky-400/5",
    positive: "border-emerald-400/30 text-emerald-300 bg-emerald-400/5",
    warning: "border-amber-400/30 text-amber-300 bg-amber-400/5",
    critical: "border-rose-400/30 text-rose-300 bg-rose-400/5",
};

const CONFIDENCE_TONE = {
    high: "text-emerald-300",
    medium: "text-amber-300",
    low: "text-rose-300",
};

export default function QaBdFunnelPage() {
    const [mode, setMode] = useState("analyze");
    const [payloadText, setPayloadText] = useState(() => JSON.stringify(DEFAULT_PAYLOAD, null, 2));
    const [insightId, setInsightId] = useState("insight_relationship_001");
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [capability, setCapability] = useState(null);

    const fetchCapability = useCallback(async () => {
        try {
            const [agentRes, flowRes] = await Promise.all([
                api.get("/admin/agents?q=bd-funnel-analytics&limit=5"),
                api.get("/admin/flows?q=bd-funnel-dashboard&limit=5"),
            ]);
            const agent = (agentRes.data?.items || []).find((a) => a.slug === "bd-funnel-analytics") || null;
            const flow = (flowRes.data?.items || []).find((f) => f.slug === "bd-funnel-dashboard") || null;
            setCapability({ agent, flow });
        } catch (e) {
            // non-fatal
        }
    }, []);

    useEffect(() => { fetchCapability(); }, [fetchCapability]);

    const parsedPayload = useMemo(() => {
        try { return { ok: true, value: JSON.parse(payloadText) }; }
        catch (e) { return { ok: false, error: e.message }; }
    }, [payloadText]);

    const onResetPayload = () => setPayloadText(JSON.stringify(DEFAULT_PAYLOAD, null, 2));

    const onRun = async () => {
        if (!parsedPayload.ok) {
            toast.error(`Invalid JSON: ${parsedPayload.error}`);
            return;
        }
        setRunning(true);
        setError(null);
        setResult(null);
        try {
            const body = { ...parsedPayload.value };
            if (mode === "insight-drilldown") body.insightId = insightId;
            const { data } = await api.post(MODE_META[mode].path, body, { timeout: 240_000 });
            setResult(data);
            toast.success(`${MODE_META[mode].label} complete`);
        } catch (e) {
            const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Request failed";
            setError(msg);
            toast.error(msg);
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="px-8 py-8" data-testid="qa-bd-funnel-page">
            <div className="flex items-start justify-between mb-7">
                <div>
                    <h1 className="text-2xl font-display tracking-tight mb-1">BD Funnel Dashboard</h1>
                    <p className="text-sm text-white/50 max-w-3xl">
                        QA testing point for the <strong>BD Funnel Analytics Agent</strong> + <strong>BD Funnel Dashboard Flow</strong>.
                        Paste a CRM / BD payload, dispatch <code className="text-white/70">analyze</code> /
                        {" "}<code className="text-white/70">insight-drilldown</code> /
                        {" "}<code className="text-white/70">print-report</code>, and inspect the dashboard-ready JSON.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={fetchCapability}
                    data-testid="qa-bd-refresh-capability"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-xs text-white/70 hover:text-white hover:bg-white/5"
                >
                    <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />Refresh capability
                </button>
            </div>

            <CapabilityPills capability={capability} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <PayloadEditor
                    mode={mode} setMode={setMode}
                    insightId={insightId} setInsightId={setInsightId}
                    payloadText={payloadText} setPayloadText={setPayloadText}
                    parseStatus={parsedPayload}
                    onReset={onResetPayload}
                    onRun={onRun} running={running}
                />
                <ResultPanel mode={mode} result={result} error={error} running={running} />
            </div>
        </div>
    );
}

// ============================================================
//  Capability pills row
// ============================================================
function CapabilityPills({ capability }) {
    const agent = capability?.agent;
    const flow = capability?.flow;
    return (
        <div className="flex flex-wrap gap-2" data-testid="qa-bd-capability-pills">
            <Pill icon={Cpu} tone="violet" label="Agent" value={agent ? `${agent.slug} · v${agent.active_version_no || 1}` : "bd-funnel-analytics (loading)"} testid="qa-bd-pill-agent" />
            <Pill icon={GitBranch} tone="sky" label="Flow" value={flow ? `${flow.slug} · v${flow.active_version_no || 1}` : "bd-funnel-dashboard (loading)"} testid="qa-bd-pill-flow" />
            <Pill icon={Database} tone="emerald" label="Billing" value="reuse_charge · source=datalake · 1/call" testid="qa-bd-pill-billing" />
            <Pill icon={CheckCircle2} tone="amber" label="Endpoints" value="/api/v1/bd-funnel/{analyze,insight-drilldown,print-report}" testid="qa-bd-pill-endpoints" />
        </div>
    );
}

function Pill({ icon: Icon, tone, label, value, testid }) {
    const palette = {
        violet: "border-violet-400/30 text-violet-200 bg-violet-400/[0.06]",
        sky: "border-sky-400/30 text-sky-200 bg-sky-400/[0.06]",
        emerald: "border-emerald-400/30 text-emerald-200 bg-emerald-400/[0.06]",
        amber: "border-amber-400/30 text-amber-200 bg-amber-400/[0.06]",
    }[tone] || "border-white/15 text-white/70";
    return (
        <div data-testid={testid} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs ${palette}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="opacity-60">{label}:</span>
            <span className="font-mono">{value}</span>
        </div>
    );
}

// ============================================================
//  Left: editor + mode picker + run controls
// ============================================================
function PayloadEditor({
    mode, setMode, insightId, setInsightId,
    payloadText, setPayloadText, parseStatus, onReset, onRun, running,
}) {
    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-white/40" strokeWidth={1.5} />
                    <h3 className="text-sm font-medium tracking-tight">Runtime payload</h3>
                </div>
                <button type="button" onClick={onReset}
                    data-testid="qa-bd-reset-payload"
                    className="text-[11px] text-white/50 hover:text-white/80">
                    Reset to sample
                </button>
            </div>

            <div className="px-5 py-4 flex flex-wrap items-center gap-3 border-b border-white/10">
                <div className="flex rounded-md border border-white/15 overflow-hidden" data-testid="qa-bd-mode-tabs">
                    {Object.entries(MODE_META).map(([k, meta]) => {
                        const Icon = meta.icon;
                        const active = mode === k;
                        return (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setMode(k)}
                                data-testid={`qa-bd-mode-${k}`}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs ${active ? "bg-[#7C3AED] text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                            >
                                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />{meta.label}
                            </button>
                        );
                    })}
                </div>
                {mode === "insight-drilldown" && (
                    <input
                        type="text"
                        value={insightId}
                        onChange={(e) => setInsightId(e.target.value)}
                        placeholder="insightId (e.g. insight_relationship_001)"
                        data-testid="qa-bd-insight-id-input"
                        className="px-3 py-1.5 rounded-md bg-black/40 border border-white/15 text-xs font-mono text-white/80 w-72 focus:outline-none focus:border-white/35"
                    />
                )}
                <div className="ml-auto flex items-center gap-2">
                    {parseStatus.ok ? (
                        <span className="text-[11px] text-emerald-300/80 font-mono">JSON ok</span>
                    ) : (
                        <span className="text-[11px] text-rose-300 font-mono" title={parseStatus.error}>JSON error</span>
                    )}
                    <button
                        type="button"
                        onClick={onRun}
                        disabled={running || !parseStatus.ok}
                        data-testid="qa-bd-run-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {running ? "Running…" : `Run ${MODE_META[mode].label}`}
                    </button>
                </div>
            </div>

            <textarea
                value={payloadText}
                onChange={(e) => setPayloadText(e.target.value)}
                spellCheck={false}
                data-testid="qa-bd-payload-textarea"
                className="block w-full h-[520px] px-5 py-4 bg-black/40 text-[12px] leading-relaxed font-mono text-white/85 focus:outline-none resize-none"
            />
        </div>
    );
}

// ============================================================
//  Right: structured result rendering
// ============================================================
function ResultPanel({ mode, result, error, running }) {
    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid="qa-bd-result-panel">
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-white/40" strokeWidth={1.5} />
                <h3 className="text-sm font-medium tracking-tight">Result</h3>
                {running && <span className="text-[11px] text-violet-300 ml-2">Calling agent…</span>}
            </div>

            <div className="px-5 py-4 max-h-[640px] overflow-y-auto">
                {error && <ErrorBlock error={error} />}
                {!error && !result && !running && <EmptyState />}
                {!error && result && mode === "analyze" && <AnalyzeResult result={result} />}
                {!error && result && mode === "insight-drilldown" && <DrilldownResult result={result} />}
                {!error && result && mode === "print-report" && <PrintResult result={result} />}
                {!error && result && (
                    <details className="mt-6 border-t border-white/10 pt-4">
                        <summary className="cursor-pointer text-[11px] text-white/40 hover:text-white/70">
                            Raw JSON
                        </summary>
                        <pre className="mt-3 text-[11px] leading-relaxed font-mono text-white/70 whitespace-pre-wrap break-all"
                            data-testid="qa-bd-raw-json">
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    </details>
                )}
            </div>
        </div>
    );
}

function ErrorBlock({ error }) {
    return (
        <div className="flex gap-3 p-4 rounded-md border border-rose-400/30 bg-rose-400/5 text-rose-200 text-xs" data-testid="qa-bd-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
                <div className="font-medium text-rose-200 mb-1">Request failed</div>
                <div className="font-mono text-rose-100/80 whitespace-pre-wrap">{error}</div>
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center text-white/40 py-16">
            <BarChart3 className="w-8 h-8 mb-3" strokeWidth={1} />
            <p className="text-xs">Run a mode to see the dashboard JSON here.</p>
        </div>
    );
}

// ============================================================
//  Analyze mode renderer
// ============================================================
function AnalyzeResult({ result }) {
    const d = result.dashboard || {};
    const warnings = result.dataQualityWarnings || [];
    const recon = result.reconciliation || {};
    const meta = result.agentMetadata || {};
    return (
        <div className="space-y-6">
            <MetaStrip meta={meta} recon={recon} />
            <Section title="KPI Summary" count={(d.kpiSummary || []).length}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {(d.kpiSummary || []).map((k, i) => <KpiTile key={i} k={k} />)}
                </div>
            </Section>
            <Section title="Key Insights" count={(d.keyInsights || []).length}>
                <div className="grid grid-cols-2 gap-3">
                    {(d.keyInsights || []).map((k, i) => <KeyInsightTile key={i} k={k} />)}
                </div>
            </Section>
            <Section title="Pipeline Overview" count={(d.pipelineOverview?.stageCards || []).length}>
                <Funnel funnel={d.pipelineOverview?.funnel || []} />
                <div className="grid grid-cols-1 gap-3 mt-3">
                    {(d.pipelineOverview?.stageCards || []).map((sc, i) => <StageCard key={i} sc={sc} />)}
                </div>
            </Section>
            <Section title="Trend Analysis" count={Object.keys(d.trendAnalysis || {}).length}>
                <TrendAnalysis t={d.trendAnalysis || {}} />
            </Section>
            <Section title="Business Development Insights" count={(d.businessDevelopmentInsights || []).length}>
                <div className="space-y-2">
                    {(d.businessDevelopmentInsights || []).map((ins, i) => <InsightTile key={i} ins={ins} />)}
                </div>
            </Section>
            {warnings.length > 0 && (
                <Section title="Data Quality Warnings" count={warnings.length} tone="amber">
                    <div className="space-y-2">
                        {warnings.map((w, i) => <WarningTile key={i} w={w} />)}
                    </div>
                </Section>
            )}
        </div>
    );
}

function MetaStrip({ meta, recon }) {
    return (
        <div className="flex flex-wrap gap-2 text-[11px] text-white/55" data-testid="qa-bd-meta-strip">
            {meta.agentName && <span className="px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 font-mono">{meta.agentName} · v{meta.agentVersion}</span>}
            {meta.llmCostUsd != null && <span className="px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 font-mono">LLM ${(meta.llmCostUsd).toFixed(4)}</span>}
            {meta.confidence && <span className={`px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 font-mono ${CONFIDENCE_TONE[meta.confidence] || ""}`}>confidence: {meta.confidence}</span>}
            {recon.recordsReceived != null && <span className="px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 font-mono">recv {recon.recordsReceived} · incl {recon.recordsIncluded} · excl {recon.recordsExcluded}</span>}
        </div>
    );
}

function Section({ title, count, children, tone }) {
    const [open, setOpen] = useState(true);
    const ChevIcon = open ? ChevronDown : ChevronRight;
    return (
        <div className={`rounded-md border ${tone === "amber" ? "border-amber-400/20" : "border-white/8"}`}>
            <button type="button" onClick={() => setOpen(!open)}
                className="w-full px-4 py-2 flex items-center justify-between text-xs text-white/65 hover:text-white">
                <span className="flex items-center gap-2">
                    <ChevIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {title}
                </span>
                <span className="text-white/35 font-mono">{count}</span>
            </button>
            {open && <div className="px-4 pb-4 pt-1">{children}</div>}
        </div>
    );
}

function KpiTile({ k }) {
    const tone = k.trendDirection === "up" ? "text-emerald-300" : k.trendDirection === "down" ? "text-rose-300" : "text-white/55";
    return (
        <div className="p-3 rounded-md border border-white/10 bg-white/[0.02]">
            <div className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-wider text-white/40">{k.label}</span>
                {k.subLabel && <span className="text-[10px] text-white/30 italic">({k.subLabel})</span>}
            </div>
            <div className="text-2xl font-display mt-1">{k.count?.toLocaleString?.() ?? k.count}</div>
            <div className={`text-[11px] mt-1 ${tone}`}>{k.displayTrend || `${k.percentChange ?? "?"}%`}</div>
        </div>
    );
}

function KeyInsightTile({ k }) {
    return (
        <div className="p-3 rounded-md border border-white/10 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-wider text-white/40">{k.label}</div>
            <div className="text-xl font-display mt-1">{k.displayValue}</div>
            <div className={`text-[11px] mt-1 ${CONFIDENCE_TONE[k.confidence] || "text-white/45"}`}>
                {k.displayTrend} · n={k.supportingRecordCount}
            </div>
        </div>
    );
}

function Funnel({ funnel }) {
    if (!funnel.length) return null;
    const max = Math.max(...funnel.map((f) => f.count || 0), 1);
    return (
        <div className="space-y-1.5">
            {funnel.map((f, i) => {
                const pct = ((f.count || 0) / max) * 100;
                return (
                    <div key={i} className="flex items-center gap-3 text-xs">
                        <span className="w-32 text-white/55">{f.label}{f.subLabel && <span className="text-white/30 ml-1">({f.subLabel})</span>}</span>
                        <div className="flex-1 h-5 rounded bg-white/5 relative overflow-hidden">
                            <div className="absolute inset-y-0 left-0 bg-violet-400/40" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-12 text-right font-mono text-white/70">{f.count}</span>
                    </div>
                );
            })}
        </div>
    );
}

function StageCard({ sc }) {
    return (
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
            <div className="text-xs text-white/70 mb-2">{sc.title}</div>
            <div className="space-y-1">
                {(sc.rows || []).map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px]">
                        <span className={`px-1.5 py-0.5 rounded-sm border ${STATUS_TONE[r.statusType] || "border-white/15 text-white/55"}`}>{r.label}</span>
                        <span className="font-mono text-white/65">{r.count} · {r.percent}%</span>
                    </div>
                ))}
            </div>
            {(sc.reasonBreakdown || []).length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
                    {sc.reasonBreakdown.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-[10px] text-white/50">
                            <span>{r.reasonLabel}</span>
                            <span className="font-mono">{r.percent}%</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TrendAnalysis({ t }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {t.bestVsLowestConversion && (
                <div className="p-3 rounded-md border border-white/10 bg-white/[0.02] md:col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Best vs Lowest Conversion</div>
                    <div className="text-xs text-white/80">{t.bestVsLowestConversion.statement}</div>
                </div>
            )}
            {(t.leadsByIndication || []).length > 0 && (
                <ListTile title="Leads by Indication" rows={t.leadsByIndication}
                    label={(r) => r.indication} value={(r) => `${r.leadCount} · ${r.percent}%`} />
            )}
            {(t.leadSourceMix || []).length > 0 && (
                <ListTile title="Lead Source Mix" rows={t.leadSourceMix}
                    label={(r) => r.label} value={(r) => `${r.count} · ${r.percent}%`} />
            )}
            {(t.croPerformanceAlerts || []).length > 0 && (
                <ListTile title="CRO Performance Alerts" rows={t.croPerformanceAlerts}
                    label={(r) => r.croName}
                    value={(r) => `${r.initiatedCount}/${r.fqSubmittedCount} (${r.fqToInitiatedRate?.toFixed?.(1) ?? r.fqToInitiatedRate}%)`} />
            )}
            {(t.sponsorPerformanceAlerts || []).length > 0 && (
                <ListTile title="Sponsor Performance Alerts" rows={t.sponsorPerformanceAlerts}
                    label={(r) => r.sponsorName}
                    value={(r) => `${r.initiatedCount}/${r.fqSubmittedCount} (${r.fqToInitiatedRate?.toFixed?.(1) ?? r.fqToInitiatedRate}%)`} />
            )}
        </div>
    );
}

function ListTile({ title, rows, label, value }) {
    return (
        <div className="p-3 rounded-md border border-white/10 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">{title}</div>
            <div className="space-y-1">
                {rows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] text-white/70">
                        <span>{label(r)}</span>
                        <span className="font-mono text-white/60">{value(r)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function InsightTile({ ins }) {
    return (
        <div className={`p-3 rounded-md border ${SEVERITY_TONE[ins.severity] || "border-white/15"}`} data-testid={`qa-bd-insight-${ins.insightId}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">{ins.type} · {ins.severity}</div>
                    <div className="text-sm font-medium">{ins.title}</div>
                    <div className="text-xs opacity-80 mt-1">{ins.statement}</div>
                </div>
                <div className={`text-[10px] ${CONFIDENCE_TONE[ins.confidence] || ""} flex-shrink-0`}>{ins.confidence}</div>
            </div>
            {ins.recommendedAction && (
                <div className="text-[11px] opacity-70 mt-2 italic">→ {ins.recommendedAction}</div>
            )}
        </div>
    );
}

function WarningTile({ w }) {
    return (
        <div className={`p-3 rounded-md border ${SEVERITY_TONE[w.severity] || "border-amber-400/20"} text-xs`}>
            <div className="flex items-center gap-2 text-[11px] opacity-80 mb-1">
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
                {w.type} · {w.severity}
            </div>
            <div className="opacity-90">{w.message}</div>
            {(w.affectedRecordIds || []).length > 0 && (
                <div className="text-[10px] opacity-50 font-mono mt-1">
                    {(w.affectedRecordIds || []).slice(0, 8).join(", ")}
                    {(w.affectedRecordIds || []).length > 8 && ` … (+${w.affectedRecordIds.length - 8})`}
                </div>
            )}
        </div>
    );
}

// ============================================================
//  Drilldown + Print mode renderers
// ============================================================
function DrilldownResult({ result }) {
    return (
        <div className="space-y-4">
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{result.insightId}</div>
                <div className="text-base font-medium">{result.title}</div>
                <div className="text-sm text-white/70 mt-2">{result.summary}</div>
                {result.metric && (
                    <div className="text-3xl font-display mt-3">
                        {result.metric.value}{result.metric.unit === "percent" ? "%" : ""}
                        <span className="text-[11px] text-white/40 ml-2 font-mono">{result.metric.name}</span>
                    </div>
                )}
                {result.confidence && <div className={`text-[11px] mt-1 ${CONFIDENCE_TONE[result.confidence]}`}>confidence: {result.confidence}</div>}
            </div>
            {(result.breakdown || []).length > 0 && (
                <ListTile title="Breakdown" rows={result.breakdown}
                    label={(r) => r.sourceCategory || r.name || "—"}
                    value={(r) => `${r.initiatedCount ?? r.count ?? ""} · ${r.percentOfInitiated ?? r.percent ?? ""}%`} />
            )}
            {(result.supportingRecords || []).length > 0 && (
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Supporting Records ({(result.supportingRecords || []).length})</div>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                        {result.supportingRecords.map((r, i) => (
                            <div key={i} className="text-[11px] text-white/65 font-mono">
                                {r.opportunityId} · {r.sponsorName} · {r.croName} · {r.indication} · GL {r.greenLightDate || "—"}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {result.recommendedAction && (
                <div className="text-xs text-white/70 italic px-3 py-2 rounded-md bg-violet-400/5 border border-violet-400/20">
                    → {result.recommendedAction}
                </div>
            )}
        </div>
    );
}

function PrintResult({ result }) {
    const r = result.report || {};
    return (
        <div className="space-y-3">
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-4">
                <div className="text-lg font-display">{r.title}</div>
                <div className="text-xs text-white/55">{r.subtitle}</div>
                <div className="text-[11px] text-white/35 mt-1 font-mono">{r.reportDate}</div>
            </div>
            {r.filters && (
                <div className="grid grid-cols-2 gap-2 text-[11px] text-white/55 font-mono">
                    {Object.entries(r.filters).map(([k, v]) => (
                        <div key={k} className="px-3 py-1.5 rounded-sm bg-white/5 border border-white/10">
                            <span className="opacity-60">{k}:</span> {String(v)}
                        </div>
                    ))}
                </div>
            )}
            {(r.sections?.definitions || []).length > 0 && (
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Definitions</div>
                    <div className="space-y-1 text-xs">
                        {r.sections.definitions.map((d, i) => (
                            <div key={i}>
                                <span className="text-white/85 font-medium">{d.term}</span>
                                <span className="text-white/55"> — {d.definition}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
